import { styled } from '@mui/material';
import { useAuth } from 'contexts/auth';
import { AnnotationMode, getDocument, GlobalWorkerOptions, version as pdfjsVersion } from 'pdfjs-dist';
import { EventBus, PDFViewer } from 'pdfjs-dist/web/pdf_viewer';
import 'pdfjs-dist/web/pdf_viewer.css';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useClickOutside from 'services/hooks/useClickOutside';
import Box from 'shell/components/Box';
import FileViewerControls from 'shell/components/FileViewerControls';
import IconButton from 'shell/components/IconButton';
import Paper from 'shell/components/Paper';
import Stack from 'shell/components/Stack';
import Tag from 'shell/components/Tag';
import Text from 'shell/components/Text';
import TextArea from 'shell/components/TextArea';
import Tooltip from 'shell/components/Tooltip';
import { normalize } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const HIGHLIGHT_CLASS_NAME = 'pdf-highlight';
const INLINE_COMMENTS_CONTAINER_WIDTH = 350;
const WORKER_SRC = `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.mjs`;

const PDFLoader = ({ children, url }) => {
    const [pdfDocument, setPdfDocument] = useState(null);
    const [error, setError] = useState(null);

    const containerRef = useRef(null);
    const pdfRef = useRef(null); // holds the loaded PDFDocumentProxy
    const loadingTaskRef = useRef(null); // holds the current PDF loading task

    useEffect(() => {
        // Set the workerSrc globally if provided
        GlobalWorkerOptions.workerSrc = WORKER_SRC;

        // Reset state before starting a new load
        setPdfDocument(null);
        setError(null);

        // Begin loading the PDF document
        const ownerDoc = containerRef.current ? containerRef.current.ownerDocument : undefined;
        const loadingTask = getDocument({ url, ownerDocument: ownerDoc });
        loadingTaskRef.current = loadingTask;
        let canceled = false;

        loadingTask.promise.then(
            (pdf) => {
                loadingTaskRef.current = null;
                if (!canceled) {
                    pdfRef.current = pdf;
                    setPdfDocument(pdf);
                } else {
                    // If the effect was canceled (component unmounted), destroy the loaded PDF to free resources
                    pdf.destroy();
                }
            },
            (err) => {
                loadingTaskRef.current = null;
                if (!canceled) setError(err);
            }
        );

        // Cleanup when the URL or options change, or on component unmount
        return () => {
            canceled = true;
            // If a PDF was loaded, destroy it to release the worker and memory
            if (pdfRef.current) {
                pdfRef.current.destroy();
                pdfRef.current = null;
            }
            // If a loading task is in progress, abort it
            if (loadingTaskRef.current) {
                loadingTaskRef.current.destroy();
                loadingTaskRef.current = null;
            }
        };
    }, [url]); // re-run if URL changes

    // Render appropriate content based on loading/error state
    return (
        <Box ref={containerRef} sx={{ position: 'relative', height: '100%' }}>
            {error || !pdfDocument ? null : typeof children === 'function' ? children(pdfDocument) : children}
        </Box>
    );
};

// Text matching logic
const findMatches = (content, target, occurrenceIndex) => {
    content = normalize(content);
    target = normalize(target);
    const sanitized = target.replace(/[^a-zA-Z0-9]/g, '');
    if (!sanitized) return [];

    const pattern = sanitized.split('').join('[^a-zA-Z0-9]*');
    const regex = new RegExp(pattern, 'gi');

    const matches = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
        matches.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
        });
        if (regex.lastIndex === match.index) regex.lastIndex++; // Prevent infinite loops
    }

    if (occurrenceIndex !== undefined) {
        return matches.filter((_, index) => index === occurrenceIndex);
    }
    return matches;
};

// Find segments from highlights and inline comments
const buildSegments = (fullText, highlights, inlineComments) => {
    const lowerText = fullText.toLowerCase();
    const ranges = [];

    highlights.forEach(({ value, index: occurrenceIndex }, index) => {
        if (!value) return;
        findMatches(lowerText, value.toLowerCase(), occurrenceIndex).forEach(({ start, end }) => {
            if (start >= end) return;
            ranges.push({ start, end, highlight: true, highlightIndex: index });
        });
    });

    inlineComments
        .filter((c) => c.active)
        .forEach(({ id, highlight, index: occurrenceIndex }) => {
            if (!highlight) return;
            findMatches(lowerText, highlight.toLowerCase(), occurrenceIndex).forEach(({ start, end }) => {
                if (start >= end) return;
                ranges.push({ start, end, commentId: id });
            });
        });

    // Generate all unique boundary points
    const points = new Set();
    ranges.forEach((r) => {
        points.add(r.start);
        points.add(r.end);
    });

    const sorted = [...points].sort((a, b) => a - b);
    const segments = [];

    // Build non-overlapping segments
    for (let i = 0; i < sorted.length - 1; i++) {
        const start = sorted[i];
        const end = sorted[i + 1];
        const covering = ranges.filter((r) => r.start <= start && r.end >= end);
        if (!covering.length) continue;

        segments.push({
            start,
            end,
            hasHighlight: covering.some((r) => r.highlight),
            highlightIndex: covering.find((r) => r.highlight)?.highlightIndex ?? null,
            commentId: covering.find((r) => r.commentId)?.commentId ?? null,
        });
    }

    return segments;
};

// Wrap text based on segment properties
const wrapSegment = (text, segment) => {
    if (!text) return '';

    const hasComment = Boolean(segment.commentId);
    const hasHighlight = segment.hasHighlight;

    if (hasComment && hasHighlight) {
        return `<mark class="${HIGHLIGHT_CLASS_NAME}" data-comment-id="${segment.commentId}" data-highlight-index="${segment.highlightIndex}">${text}</mark>`;
    }
    if (hasComment) {
        return `<span style="position: relative;" data-comment-id="${segment.commentId}">${text}</span>`;
    }
    if (hasHighlight) {
        return `<mark class="${HIGHLIGHT_CLASS_NAME}" data-highlight-index="${segment.highlightIndex}">${text}</mark>`;
    }
    return text;
};

// Render segments into individual spans
const renderSegmentsIntoSpans = (textDivs, textContents, prefixSums, segments) => {
    let segIdx = 0;

    textDivs.forEach((span, spanIndex) => {
        const spanText = textContents[spanIndex];
        if (!spanText) return;

        const spanStart = prefixSums[spanIndex - 1] || 0;
        const spanEnd = spanStart + spanText.length;

        // Find all segments intersecting this span
        const spanSegments = [];
        while (segIdx < segments.length && segments[segIdx].end <= spanStart) segIdx++;
        let i = segIdx;
        while (i < segments.length && segments[i].start < spanEnd) {
            spanSegments.push(segments[i]);
            i++;
        }

        // If no segments, keep plain
        if (!spanSegments.length) {
            span.innerHTML = spanText;
            return;
        }

        // Build HTML for this span
        let html = '';
        let cursor = spanStart;
        spanSegments.forEach((seg) => {
            const segStart = Math.max(seg.start, spanStart);
            const segEnd = Math.min(seg.end, spanEnd);

            // Plain text before segment
            if (cursor < segStart) {
                html += spanText.slice(cursor - spanStart, segStart - spanStart);
            }

            // Wrapped segment text
            const segmentText = spanText.slice(segStart - spanStart, segEnd - spanStart);
            html += wrapSegment(segmentText, seg);
            cursor = segEnd;
        });

        // Trailing plain text
        if (cursor < spanEnd) {
            html += spanText.slice(cursor - spanStart);
        }

        span.innerHTML = html;
    });
};

const renderTextHighlights = (textDivs, highlights = [], inlineComments = []) => {
    if (!Array.isArray(textDivs) || !textDivs.length) return;

    // Reset all spans
    textDivs.forEach((span) => (span.innerHTML = span.textContent || ''));

    // Prepare text data
    const textContents = textDivs.map((span) => span.textContent || '');
    const fullText = textContents.join('');
    const lengths = textContents.map((t) => t.length);
    const prefixSums = lengths.reduce((acc, len, i) => {
        acc.push((acc[i - 1] || 0) + len);
        return acc;
    }, []);

    // Build segments and render
    const segments = buildSegments(fullText, highlights, inlineComments);
    if (!segments.length) return;

    renderSegmentsIntoSpans(textDivs, textContents, prefixSums, segments);
};

const PDFHighlighter = memo(
    ({
        url,
        document: pdfDocument,
        highlights,
        inlineComments,
        setInlineComments,
        selectedInlineCommentId,
        setSelectedInlineCommentId,
        isFullScreen,
        setIsFullScreen,
    }) => {
        const { auth } = useAuth();

        const containerRef = useRef(null);
        const viewerRef = useRef(null);
        const eventBusRef = useRef(null);
        const annotationsStackRef = useRef(null);

        // map of text divs used for rerendering highlights
        const [textDivs, setTextDivs] = useState({});
        // current page number being displayed in the PDF viewer (1-indexed)
        const [currentPage, setCurrentPage] = useState(1);
        // the "base" scale representing the page-width zoom level (used as a reference scale)
        const [baseScale, setBaseScale] = useState(1);
        // the actual current scale applied to the PDF pages (may differ from baseScale due to zoomFactor)
        const [currentScale, setCurrentScale] = useState(1);
        // the relative zoom factor applied on top of baseScale (1 = 100% of page-width, 0.5 = 50%, etc.)
        const [zoomFactor, setZoomFactor] = useState(1);
        // mutable ref for zoomFactor to have a stable value inside effects and callbacks without re-renders
        const zoomFactorRef = useRef(zoomFactor);
        // visibility toggle
        const [isHighlightVisible, setIsHighlightVisible] = useState(true);
        // final Y positions for rendering comment boxes, adjusted to avoid overlap
        const [commentPositions, setCommentPositions] = useState({});
        // raw Y positions of each highlight in the PDF container
        const [highlightTops, setHighlightTops] = useState({});
        // mutable ref to track inline comments positioning
        const commentRefs = useRef({});
        // the id of the comment being edited, empty string means no comment is in editing mode
        const [editableCommentId, setEditableCommentId] = useState('');

        const zoomPercent = Math.round((currentScale / baseScale) * 100);
        const numCommentRefsStored = Object.keys(commentRefs?.current || {}).length;
        const inlineCommentScaling = inlineComments.length ? 0.9 : 1; // scaling applied when inline comments exist

        const clampZoom = (factor) => Math.min(Math.max(factor, 0.25), 4); // 25% to 400%

        const setZoom = (factor) => {
            zoomFactorRef.current = factor;
            setZoomFactor(factor);
        };

        const onZoomIn = useCallback(() => {
            if (!viewerRef.current || !baseScale) return;

            const newFactor = clampZoom(zoomFactor + 0.25);
            const newScale = newFactor * baseScale;

            viewerRef.current.currentScale = newScale;
            setZoom(newFactor);
            setCurrentScale(newScale);
        }, [baseScale, zoomFactor]);

        const onZoomOut = useCallback(() => {
            if (!viewerRef.current || !baseScale) return;

            const newFactor = clampZoom(zoomFactor - 0.25);
            const newScale = newFactor * baseScale;

            viewerRef.current.currentScale = newScale;
            setZoom(newFactor);
            setCurrentScale(newScale);
        }, [baseScale, zoomFactor]);

        const updateCommentPositions = useCallback(() => {
            if (!containerRef.current) return;

            const containerTop = containerRef.current.getBoundingClientRect().top;

            const rawPositionsById = {};
            const boxHeightsById = {};

            inlineComments
                .filter(({ active }) => active)
                .forEach((comment) => {
                    const { id } = comment;
                    // Find the anchor element with the matching data-comment-id
                    const anchor = containerRef.current.querySelector(`[data-comment-id="${id}"]`);
                    if (!anchor) return;

                    // Calculate the top relative to container (accounting for scroll)
                    const rect = anchor.getBoundingClientRect();
                    const top = rect.top - containerTop + containerRef.current.scrollTop;
                    rawPositionsById[id] = top;

                    // Get the height of the comment box for spacing
                    const box = commentRefs.current[id];
                    boxHeightsById[id] = box?.getBoundingClientRect().height || 100;
                });

            // Sort comments by their raw top positions
            const idsInOrder = Object.entries(rawPositionsById)
                .sort((a, b) => a[1] - b[1])
                .map(([id]) => id);

            const adjustedById = {};

            idsInOrder.forEach((id, i) => {
                if (i === 0) {
                    adjustedById[id] = rawPositionsById[id];
                } else {
                    const prevId = idsInOrder[i - 1];
                    const prevBottom = adjustedById[prevId] + boxHeightsById[prevId] + 12; // 12px gap
                    adjustedById[id] = Math.max(rawPositionsById[id], prevBottom);
                }
            });

            setHighlightTops(rawPositionsById);
            setCommentPositions(adjustedById);
        }, [inlineComments]);

        const onCreateCommentFromSelection = useCallback(
            ({ selectedText, pageNumber }) => {
                const newComment = {
                    id: uuid(),
                    active: true,
                    owner_account_id: auth.id,
                    page_num: pageNumber,
                    highlight: selectedText,
                    content: '',
                };
                setInlineComments((current) => {
                    return {
                        ...current,
                        inline_comments: [...(current?.inline_comments || []), newComment],
                    };
                });
                setSelectedInlineCommentId(newComment.id);
                setEditableCommentId(newComment.id);
            },
            [auth.id, setInlineComments, setSelectedInlineCommentId]
        );

        const onClickCreateComment = useCallback(() => {
            const container = containerRef.current;
            if (!container) return;
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed) return;
            const selectedText = selection.toString().trim();
            if (!selectedText) return;
            if (!container.contains(selection.anchorNode)) return;

            const anchorNode = selection.anchorNode;
            if (!anchorNode) return;
            const pageDiv = anchorNode.parentElement?.closest('.page');
            if (!pageDiv) return;
            const pageNumber = parseInt(pageDiv.getAttribute('data-page-number'), 10);

            onCreateCommentFromSelection({ selectedText, pageNumber });
        }, [onCreateCommentFromSelection]);

        useEffect(() => {
            zoomFactorRef.current = zoomFactor;
        }, [zoomFactor]);

        // init
        useEffect(() => {
            if (!pdfDocument || !containerRef.current) return;

            const eventBus = new EventBus();
            const viewer = new PDFViewer({
                container: containerRef.current,
                eventBus,
                removePageBorders: true,
                annotationMode: AnnotationMode.ENABLE_FORMS,
                useOnlyCssZoom: true,
            });

            const handlePagesInit = () => {
                viewer.currentScaleValue = 'page-width';
                requestAnimationFrame(() => {
                    const scale = viewer.currentScale;
                    setBaseScale(inlineCommentScaling * scale);
                    setCurrentScale(inlineCommentScaling * scale);
                });
            };

            const handlePageChange = (evt) => {
                setCurrentPage(evt.pageNumber);
            };

            eventBus.on('pagesinit', handlePagesInit);
            eventBus.on('pagechanging', handlePageChange);
            viewer.setDocument(pdfDocument);

            viewerRef.current = viewer;
            eventBusRef.current = eventBus;

            return () => {
                eventBus.off('pagesinit', handlePagesInit);
                eventBus.off('pagechanging', handlePageChange);
                if (viewer?.cleanup) viewer.cleanup();
                viewerRef.current = null;
                eventBusRef.current = null;
            };
        }, [inlineCommentScaling, pdfDocument]);

        // handle viewport resizing
        useEffect(() => {
            if (!containerRef.current || !viewerRef.current) return;

            let resizeTimeout;
            const observerTarget = containerRef.current;

            const resizeObserver = new ResizeObserver(() => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    if (!viewerRef.current) return;
                    viewerRef.current.currentScaleValue = 'page-width';

                    requestAnimationFrame(() => {
                        if (!viewerRef.current) return;

                        const newBase = inlineCommentScaling * viewerRef.current.currentScale;
                        const newScale = zoomFactorRef.current * newBase;
                        const isSameScale = Math.abs(viewerRef.current.currentScale - newScale) < 0.01;

                        if (!isSameScale) {
                            setBaseScale(newBase);
                            setCurrentScale(newScale);
                            viewerRef.current.currentScale = newScale;
                        }
                    });
                }, 10);
            });

            resizeObserver.observe(observerTarget);

            return () => {
                resizeObserver.disconnect();
                clearTimeout(resizeTimeout);
            };
        }, [inlineCommentScaling]);

        // update comment positions as pages are rerendered
        useEffect(() => {
            const eventBus = eventBusRef.current;
            if (!eventBus) return;

            const handleTextLayerRendered = (event) => {
                setTextDivs((current) => ({
                    ...current,
                    [event.source.textLayer.highlighter.pageIdx + 1]: event.source.textLayer.highlighter.textDivs,
                }));
                // renderTextHighlights(
                //     event.source.textLayer.highlighter.pageIdx + 1,
                //     event.source.textLayer.highlighter.textDivs,
                //     isHighlightVisible ? highlights : [],
                //     inlineComments
                // );
                // updateCommentPositions();
            };

            eventBus.on('textlayerrendered', handleTextLayerRendered);
            return () => {
                eventBus.off('textlayerrendered', handleTextLayerRendered);
            };
        }, []);

        // // rerender highlights when highlights changes
        // useEffect(() => {
        //     Object.entries(textDivs).forEach(([pageNumber, textDivs]) => {
        //         renderTextHighlights(pageNumber, textDivs, isHighlightVisible ? highlights : [], inlineComments);
        //     });
        // }, [highlights, inlineComments, isHighlightVisible, textDivs]);

        useEffect(() => {
            const combinedTextDivs = Object.entries(textDivs)
                .sort(([aPageNumber], [bPageNumber]) => {
                    return Number(aPageNumber) - Number(bPageNumber);
                })
                .map(([_, content]) => {
                    return content;
                })
                .flat();

            renderTextHighlights(combinedTextDivs, isHighlightVisible ? highlights : [], inlineComments);
            updateCommentPositions();
        }, [highlights, inlineComments, isHighlightVisible, textDivs, updateCommentPositions]);

        // manually dispatch textlayerrendered event when zoom is changed
        useEffect(() => {
            const viewer = viewerRef.current;
            const eventBus = eventBusRef.current;
            if (!viewer || !eventBus) return;

            viewer._pages?.forEach((pageView, pageIndex) => {
                const textLayer = pageView?.textLayer;
                if (!textLayer?.textDivs?.length) return;

                eventBus.dispatch('textlayerrendered', {
                    source: textLayer,
                    pageNumber: pageIndex + 1,
                });
            });
        }, [currentScale]);

        // update comment positions when more comments are rendered
        useEffect(() => {
            updateCommentPositions();
        }, [numCommentRefsStored, updateCommentPositions]);

        useEffect(() => {
            if (selectedInlineCommentId && !inlineComments.find(({ id }) => id === selectedInlineCommentId).active) {
                setSelectedInlineCommentId(null);
            }
        }, [inlineComments, selectedInlineCommentId, setSelectedInlineCommentId]);

        useClickOutside(annotationsStackRef, () => {
            setSelectedInlineCommentId(null);
        });

        return (
            <>
                <FileViewerControls
                    url={url}
                    inlineComments={inlineComments}
                    currentPageNum={currentPage}
                    totalNumPages={pdfDocument.numPages}
                    zoomPercent={zoomPercent}
                    isFullScreen={isFullScreen}
                    setIsFullScreen={setIsFullScreen}
                    onZoomIn={onZoomIn}
                    onZoomOut={onZoomOut}
                    isHighlightVisible={isHighlightVisible}
                    setIsHighlightVisible={setIsHighlightVisible}
                    onClickCreateComment={onClickCreateComment}
                />
                <ContainerStack ref={containerRef} direction={'row'} tabIndex={0}>
                    <Box className={'pdfViewer'} tabIndex={-1} />
                    {inlineComments.length ? (
                        <AnnotationsStack
                            ref={annotationsStackRef}
                            spacing={4}
                            onClick={(e) => {
                                const clickedInsideAComment = e.target.closest('[data-comment-box]');
                                if (!clickedInsideAComment) {
                                    setSelectedInlineCommentId(null);
                                }
                            }}
                        >
                            {inlineComments
                                .filter(({ active }) => active)
                                .map((inlineComment, index) => {
                                    const top =
                                        selectedInlineCommentId === inlineComment.id
                                            ? highlightTops[inlineComment.id]
                                            : commentPositions[inlineComment.id];
                                    return (
                                        <EditableAnnotation
                                            key={inlineComment.id}
                                            index={index}
                                            inlineComment={inlineComment}
                                            commentRefs={commentRefs}
                                            top={top}
                                            selectedInlineCommentId={selectedInlineCommentId}
                                            setSelectedInlineCommentId={setSelectedInlineCommentId}
                                            isEditing={editableCommentId === inlineComment.id}
                                            setIsEditing={(editable) => {
                                                setEditableCommentId(editable ? inlineComment.id : '');
                                            }}
                                            setInlineComments={setInlineComments}
                                        />
                                    );
                                })}
                        </AnnotationsStack>
                    ) : null}
                </ContainerStack>
            </>
        );
    }
);

const EditableAnnotation = ({
    inlineComment,
    commentRefs,
    top,
    selectedInlineCommentId,
    setSelectedInlineCommentId,
    isEditing,
    setIsEditing,
    setInlineComments,
}) => {
    const { t } = useTranslation('assignment');

    const isSelected = selectedInlineCommentId === inlineComment.id;
    const isNothingSelected = selectedInlineCommentId === null;

    if (!top) return null;

    return (
        <AnnotationPaper
            ref={(el) => {
                if (el) commentRefs.current[inlineComment.id] = el;
                else delete commentRefs.current[inlineComment.id];
            }}
            data-comment-box
            $isSelected={isSelected}
            $isNothingSelected={isNothingSelected}
            tabIndex={0}
            top={top}
            variant={'outlined'}
            onClick={() => setSelectedInlineCommentId(inlineComment.id)}
        >
            <Stack spacing={3}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            {t('inline-comments.comment-title', 'Comment')}
                        </Text>
                        {!inlineComment.owner_account_id && (
                            <Tooltip
                                enterDelay={500}
                                title={
                                    <Text color={'inherit'} variant={'p3'}>
                                        {t(
                                            'inline-comments.ai-disclaimer',
                                            'This content was created using AI to assist in the process.'
                                        )}
                                    </Text>
                                }
                            >
                                <Tag type={'ai'} value={'assisted'} />
                            </Tooltip>
                        )}
                    </Stack>
                    <IconButton
                        aria-label={'delete comment'}
                        kind={'delete'}
                        size={'small'}
                        variant={'square'}
                        onClick={(e) => {
                            e.stopPropagation();
                            setInlineComments((current) => {
                                return {
                                    ...current,
                                    inline_comments: current.inline_comments.map((ic) => {
                                        return ic.id !== inlineComment.id ? ic : { ...ic, active: false };
                                    }),
                                };
                            });
                        }}
                    />
                </Stack>
                {isEditing ? (
                    <TextArea
                        autoFocus
                        defaultValue={inlineComment.content}
                        onBlur={(e) => {
                            setInlineComments((current) => ({
                                ...current,
                                inline_comments: current.inline_comments.map((item) => {
                                    if (item.id !== inlineComment.id) return item;
                                    const newValue = e.target.value.trim();
                                    return newValue ? { ...item, content: newValue } : { ...item, active: false };
                                }),
                            }));
                            setIsEditing(false);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <CustomText
                        align={'left'}
                        rich
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                setSelectedInlineCommentId(inlineComment.id);
                                setIsEditing(true);
                            }
                        }}
                        onClick={() => setIsEditing(true)}
                    >
                        {inlineComment.content}
                    </CustomText>
                )}
            </Stack>
        </AnnotationPaper>
    );
};

const PDFViewerComponent = ({
    url,
    highlights,
    inlineComments,
    setInlineComments,
    selectedInlineCommentId,
    setSelectedInlineCommentId,
    isFullScreen,
    setIsFullScreen,
}) => {
    useEffect(() => {
        if (highlights.length) {
            const els = document.getElementsByTagName('mark');
            if (els?.length) {
                els[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(highlights.map((h) => h.value))]);

    return (
        <RootStack className={'joyride-step-7'} $selectedInlineCommentId={selectedInlineCommentId}>
            <PDFLoader url={url}>
                {(document) => (
                    <PDFHighlighter
                        url={url}
                        document={document}
                        highlights={highlights}
                        inlineComments={inlineComments || []}
                        setInlineComments={setInlineComments}
                        selectedInlineCommentId={selectedInlineCommentId}
                        setSelectedInlineCommentId={setSelectedInlineCommentId}
                        isFullScreen={isFullScreen}
                        setIsFullScreen={setIsFullScreen}
                    />
                )}
            </PDFLoader>
        </RootStack>
    );
};

const RootStack = styled(Stack)(({ theme, $selectedInlineCommentId }) => {
    return {
        height: '100%',
        [`& .${HIGHLIGHT_CLASS_NAME}`]: {
            borderBottom: $selectedInlineCommentId
                ? `1px solid ${theme.palette.ensightful.success[700]}`
                : `1px solid ${theme.palette.ensightful.warning[700]}`,
            background: $selectedInlineCommentId
                ? `${theme.palette.ensightful.success[500]}40`
                : `${theme.palette.ensightful.warning[500]}40`,
            color: 'transparent',
        },
    };
});

const ContainerStack = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        width: '100%',
        height: 'calc(100% - 32px)',
        overflow: 'auto',
        background: theme.palette.ensightful.neutral[100],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
        '& div.page:last-child': {
            marginBottom: 0,
        },
        '& mark': {
            scrollMarginTop: '50px',
        },
    };
});

const AnnotationsStack = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        flexShrink: 0,
        width: `${INLINE_COMMENTS_CONTAINER_WIDTH}px`,
        padding: theme.spacing(4),
    };
});

const AnnotationPaper = styled(Paper)(({ theme, top, $isSelected, $isNothingSelected }) => {
    return {
        position: 'absolute',
        top,
        zIndex: $isSelected || $isNothingSelected ? 1 : 0,
        width: `calc(100% - ${theme.spacing(8)})`,
        boxShadow: theme.palette.ensightful.shadows[1],
        cursor: 'pointer',
        opacity: $isSelected || $isNothingSelected ? 1 : 0.5,
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
        ...($isSelected && {
            border: `2px solid ${theme.palette.ensightful.success[700]}`,
            margin: '-1px',
            width: `calc(100% - ${theme.spacing(8)} + 1px)`,
            paddingRight: '15px',
        }),
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        '&:focus-visible': {
            borderRadius: '4px',
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

export default PDFViewerComponent;
